// Grids & Gantts — sorting a TREE by one of its columns.
//
// Standard 1.8: A SORT REORDERS SIBLINGS. IT NEVER FLATTENS.
//
// Clicking a column heading on an ordinary table sorts the rows. On a
// structure it cannot: the rows are a tree, and putting them in value order
// would throw away the parents, the folders and the groups that are the whole
// point of the page. What a reader means by "sort by due date" here is
// "inside each folder, and under each parent, put them in due-date order".
//
// So the flattened row list is re-ordered in place, one sibling group at a
// time, with every child following the row it belongs to. A row's parent is
// its `path` less the last segment, which is how the grid already identifies
// a row, so nothing new has to be carried to make this work.
//
// Pure, and tested under node: the part that can be wrong is "did a child end
// up under a different parent", and that is not something you can see by
// looking at a screen.

// What one row is worth in one column. The grid already knows — `cellOf` is
// the single place that answers what a cell CONTAINS — so the comparable
// value comes from there rather than from a second, drifting copy.
export function sortKeyOf(cell) {
  if (!cell) return { n: null, s: '' };
  // A number sorts as a number; everything else sorts as text, case-folded.
  if (cell.num != null && Number.isFinite(cell.num)) return { n: cell.num, s: '' };
  const raw = cell.raw != null ? cell.raw : cell.text;
  const s = String(raw == null ? '' : raw);
  return { n: null, s: s.toLowerCase() };
}

// EMPTY SORTS LAST, ascending or descending. A blank is not "before
// everything" — it is "not answered", and a reader sorting by due date wants
// the dated work, not forty blanks at the top.
export function compareKeys(a, b, dir = 'asc') {
  const aEmpty = a.n == null && !a.s;
  const bEmpty = b.n == null && !b.s;
  if (aEmpty || bEmpty) {
    if (aEmpty && bEmpty) return 0;
    return aEmpty ? 1 : -1;
  }
  let d = 0;
  if (a.n != null && b.n != null) d = a.n - b.n;
  else d = a.s < b.s ? -1 : (a.s > b.s ? 1 : 0);
  return dir === 'desc' ? -d : d;
}

const parentPath = (path) => {
  const s = String(path || '');
  const i = s.lastIndexOf('/');
  return i < 0 ? '' : s.slice(0, i);
};

// The flattened rows, re-ordered. `keyFor(row)` gives the value to compare;
// rows it cannot answer for (a generator line, a group header) keep their
// place relative to their own kind.
export function sortRows(rows, keyFor, dir = 'asc') {
  const list = rows || [];
  if (!list.length || !dir) return list;

  // Each row's children, in the order they arrive. A row's descendants are
  // the block that follows it until a row at its own depth or shallower.
  const index = new Map();
  list.forEach((r, i) => index.set(r.path, i));

  const childrenOf = new Map();
  const roots = [];
  list.forEach((r) => {
    const p = parentPath(r.path);
    if (p && index.has(p)) {
      if (!childrenOf.has(p)) childrenOf.set(p, []);
      childrenOf.get(p).push(r);
    } else {
      roots.push(r);
    }
  });

  const sortGroup = (group) => {
    // A RULE LINE IS NOT A ROW TO SORT. Generators are printed above what
    // they generate and belong where they are, whatever the sort says.
    const rules = group.filter((r) => r.kind === 'rule');
    const rest = group.filter((r) => r.kind !== 'rule');
    const keyed = rest.map((r, i) => ({ r, i, k: keyFor(r) }));
    keyed.sort((x, y) => compareKeys(x.k, y.k, dir) || (x.i - y.i));
    return [...rules, ...keyed.map((x) => x.r)];
  };

  const out = [];
  const walk = (group) => {
    sortGroup(group).forEach((r) => {
      out.push(r);
      const kids = childrenOf.get(r.path);
      if (kids && kids.length) walk(kids);
    });
  };
  walk(roots);
  // A row whose parent was not in the list at all would otherwise vanish;
  // the count must come out the same or the sort has eaten something.
  return out.length === list.length ? out : list;
}
